import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef, useMemo } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft, FiUpload, FiX } from 'react-icons/fi';
import { Card, Button, Input } from '@/components';
import { apiJson, ApiError } from '@/lib/api';
import {
  isValidCurrencyInput,
  dollarsToCents,
  formatDateLocal,
} from '@/lib/utils';
import type {
  PaymentMethod,
  PaymentMethodListResponse,
  UserResponse,
  HouseholdResponse,
  PresignedUrlResponse,
} from '@/lib/types';

function RecordPaymentPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track screen view
  useEffect(() => {
    track('Record Payment Screen');
  }, []);

  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(() =>
    formatDateLocal(new Date())
  );
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null
  );
  const [paidBy, setPaidBy] = useState<'you' | 'coparent'>('you');
  const [note, setNote] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState('');

  // Fetch user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  // Fetch households
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  // Fetch payment methods
  const paymentMethodsQuery = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () =>
      apiJson<PaymentMethodListResponse>(getToken, '/v1/payment-methods'),
  });

  const paymentMethods = useMemo(
    () => paymentMethodsQuery.data?.payment_methods ?? [],
    [paymentMethodsQuery.data?.payment_methods]
  );
  const currentUser = userQuery.data;
  const households = householdsQuery.data ?? [];
  const firstHousehold = households[0];
  const members = firstHousehold?.members ?? [];
  const coparent = members.find((m) => m.user_id !== currentUser?.id);
  const coparentName = coparent?.first_name ?? 'Co-parent';

  // Set default payment method
  useEffect(() => {
    if (paymentMethods.length > 0 && !selectedMethod) {
      const venmo = paymentMethods.find(
        (m) => m.name.toLowerCase() === 'venmo'
      );
      setSelectedMethod(venmo ?? paymentMethods[0]);
    }
  }, [paymentMethods, selectedMethod]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || isValidCurrencyInput(value)) {
      setAmount(value);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (selectedFiles.length + files.length > 3) {
      setError('You can attach up to 3 images.');
      return;
    }
    setSelectedFiles([
      ...selectedFiles,
      ...files.slice(0, 3 - selectedFiles.length),
    ]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  // Upload file to presigned URL
  const uploadFile = async (paymentId: number, file: File): Promise<void> => {
    const presignedResponse = await apiJson<PresignedUrlResponse>(
      getToken,
      `/v1/payments/${paymentId}/attachments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: file.name,
          attachment_type: file.type,
        }),
      }
    );

    await fetch(presignedResponse.presigned_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      setError('');
      const amountCents = dollarsToCents(amount);
      if (!amountCents) throw new Error('Please enter a valid amount.');
      if (!selectedMethod) throw new Error('Please select a payment method.');
      if (!firstHousehold) throw new Error('No household found.');
      if (!currentUser) throw new Error('User not ready.');
      if (!coparent) throw new Error('No co-parent found.');

      // Determine sender and recipient
      let sender_user_id: number;
      let recipient_user_id: number;
      if (paidBy === 'you') {
        sender_user_id = currentUser.id;
        recipient_user_id = coparent.user_id;
      } else {
        sender_user_id = coparent.user_id;
        recipient_user_id = currentUser.id;
      }

      const payload = {
        household_id: firstHousehold.id,
        payment_date: new Date(paymentDate).toISOString(),
        amount_cents: amountCents,
        payment_method: selectedMethod.payment_method_id,
        sender_user_id,
        recipient_user_id,
        note: note.trim() || null,
      };

      const payment = await apiJson<{ id: number }>(getToken, '/v1/payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // Upload attachments
      for (const file of selectedFiles) {
        await uploadFile(payment.id, file);
      }

      return payment;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['payments'] });
      await queryClient.invalidateQueries({
        queryKey: ['accounting', 'summary'],
      });
      navigate({ to: '/app/expenses' });
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't save payment.";
      setError(errorMessage);
    },
  });

  const isFormValid = dollarsToCents(amount) && selectedMethod && coparent;

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Mobile Header - hidden on desktop */}
      <header className='lg:hidden bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4'>
          <Link
            to='/app/expenses'
            className='inline-flex items-center gap-2 text-primary hover:underline'
          >
            <FiArrowLeft /> Back
          </Link>
        </div>
      </header>

      {/* Desktop Header - shown only on large screens */}
      <header className='hidden lg:block bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-8 py-4'>
          <h1
            className='text-xl font-semibold text-gray-900'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Add Payment
          </h1>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6'>
        <p className='text-gray-500 text-center lg:text-left mb-6'>
          Record a payment to or from your co-parent.
        </p>

        <Card className='space-y-5'>
          {/* Amount */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>
              Amount
            </label>
            <Input
              value={amount}
              onChange={handleAmountChange}
              placeholder='$50.00'
              disabled={mutation.isPending}
            />
          </div>

          {/* Date */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>
              Date
            </label>
            <input
              type='date'
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              max={formatDateLocal(new Date())}
              disabled={mutation.isPending}
              className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>
              Payment method
            </label>
            <select
              value={selectedMethod?.payment_method_id ?? ''}
              onChange={(e) => {
                const method = paymentMethods.find(
                  (m) => m.payment_method_id === e.target.value
                );
                setSelectedMethod(method ?? null);
              }}
              disabled={mutation.isPending || paymentMethodsQuery.isLoading}
              className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
            >
              {paymentMethods.map((method) => (
                <option
                  key={method.payment_method_id}
                  value={method.payment_method_id}
                >
                  {method.name}
                </option>
              ))}
            </select>
          </div>

          {/* Paid by */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>
              Paid by
            </label>
            <div className='flex rounded-2xl border border-gray-200 overflow-hidden'>
              <button
                onClick={() => setPaidBy('you')}
                disabled={mutation.isPending}
                className={`flex-1 py-3 text-center font-semibold transition-colors ${
                  paidBy === 'you'
                    ? 'bg-primary text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                You
              </button>
              <button
                onClick={() => setPaidBy('coparent')}
                disabled={mutation.isPending}
                className={`flex-1 py-3 text-center font-semibold transition-colors ${
                  paidBy === 'coparent'
                    ? 'bg-primary text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {coparentName}
              </button>
            </div>
          </div>

          {/* Note */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>
              Note (optional)
            </label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='Any details...'
              disabled={mutation.isPending}
            />
          </div>

          {/* Attachments */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>
              Attachments (optional)
            </label>
            <div className='flex flex-wrap gap-3'>
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className='relative w-24 h-24 rounded-xl overflow-hidden border border-gray-200'
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className='w-full h-full object-cover'
                  />
                  <button
                    onClick={() => removeFile(index)}
                    disabled={mutation.isPending}
                    className='absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors'
                  >
                    <FiX className='w-3 h-3' />
                  </button>
                </div>
              ))}
              {selectedFiles.length < 3 && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={mutation.isPending}
                  className='w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-primary hover:border-primary hover:bg-primary/5 transition-colors'
                >
                  <FiUpload className='w-5 h-5 mb-1' />
                  <span className='text-xs font-semibold'>Add Photo</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type='file'
                accept='image/*'
                onChange={handleFileSelect}
                className='hidden'
              />
            </div>
          </div>

          {!coparent && firstHousehold && (
            <p className='text-sm text-amber-600 text-center'>
              No co-parent found. Please invite your co-parent before recording
              a payment.
            </p>
          )}

          {error && <p className='text-sm text-red-500 text-center'>{error}</p>}

          <Button
            fullWidth
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !isFormValid}
            loading={mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save payment'}
          </Button>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/payments/record')({
  component: RecordPaymentPage,
});
